import { Skeleton } from "@mui/material";
import { useEffect, useState } from "react";
import SpecialistApi from "../../../api/specialistsApi";
import CardSkeleton from "../../../components/CardSkeleton/CardSkeleton";
import { SessionPackage } from "../../../types/TypeSession";
import { SpecialistData } from "../../../types/TypeSpecialist";
import { TypeUserData } from "../../../types/TypeUsers";
import { getTranslation } from "../../../utils/getTranslation";
import PackageCard from "../../SessionPackages/PackageCard/PackageCard";
import s from "./PackagesSection.module.css";

type PackagesSectionProps = {
  profileUserData: TypeUserData;
  specialistData: SpecialistData;
};

const PackagesSection = ({
  profileUserData,
  specialistData,
}: PackagesSectionProps) => {
  const token = localStorage.getItem("token");
  const [packagesData, setPackagesData] = useState<
    SessionPackage[] | undefined
  >();
  const [isLoading, setIsLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);
  const [totalListElements, setTotalListElements] = useState(0);

  useEffect(() => {
    if (!packagesData) return;
    const totalCount = packagesData.length || 0;

    setTotalListElements(totalCount);
    setHasOverflow(totalCount > 2);
  }, [packagesData]);

  useEffect(() => {
    const makeAsync = async () => {
      if (!token) return;
      setIsLoading(true);
      const response = await SpecialistApi.getSpecialistSessionPackages(
        token,
        profileUserData._id,
        specialistData._id
      );

      if (response.status && response.packages) {
        setPackagesData(response.packages);
      }
      setIsLoading(false);
    };
    makeAsync();
  }, [profileUserData, specialistData]);

  const fullName =
    getTranslation(profileUserData.name, "en") +
    " " +
    getTranslation(profileUserData.surname, "en");

  const blockTitleData = {
    Coach: "Coaching session packages from",
    Psychotherapist: "Therapy session packages from",
    Mentor: "Mentoring session packages from",
  };

  if (isLoading) {
    return (
      <div className={s.container}>
        <Skeleton variant="text" width="60%" height={30} />
        <Skeleton variant="text" width="40%" height={20} />
        <Skeleton
          variant="text"
          width="100%"
          height={20}
          sx={{ marginBottom: 2 }}
        />
        <CardSkeleton />
        <CardSkeleton />
      </div>
    );
  }

  if (!packagesData || !packagesData.length) return null;

  return (
    <div className={s.container}>
      <h1 className={s.blockTitle}>
        {
          blockTitleData[
            specialistData.mainSpecializations[0]
              .specialization as keyof typeof blockTitleData
          ]
        }{" "}
        <span className={s.stronTextLeftBlok}>{fullName}</span>
      </h1>
      <div className={s.expandableWrapper}>
        {packagesData
          .slice(0, !isExpanded ? 2 : packagesData.length)
          .map((item) => (
            <PackageCard sessionPackage={item} key={item._id} />
          ))}
      </div>
      {hasOverflow && (
        <button
          className={s.toggleButton}
          onClick={() => setIsExpanded((prev) => !prev)}
        >
          {isExpanded ? "Collapse" : `View all (${totalListElements})`}
        </button>
      )}
    </div>
  );
};

export default PackagesSection;
